import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import AddCommentIcon from '@material-ui/icons/AddComment';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import BarChartIcon from '@material-ui/icons/BarChart';

import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import FilterListIcon from '@material-ui/icons/FilterList';

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsListCustom";
import TabPanel from "../TabPanel";
import TicketSectionModal from "../TicketSectionModal";
import api from "../../services/api";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import ConfirmationModal from "../ConfirmationModal";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { Button, IconButton, Typography } from "@material-ui/core";
import { TagsFilter } from "../TagsFilter";
import { UsersFilter } from "../UsersFilter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPeopleGroup } from '@fortawesome/free-solid-svg-icons';
import useSettings from "../../hooks/useSettings";
import { WhatsAppFilter } from "../WhatsAppFilter";
import { toast } from "react-toastify";
import { ChannelsFilter } from "../MediaFilter";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
    [theme.breakpoints.down("sm")]: {
      borderRadius: 0,
    },
  },

  tabsHeader: {
    flex: "none",
    backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
    borderBottom: theme.palette.type === "dark" 
      ? "1px solid rgba(255,255,255,0.1)" 
      : "1px solid #e2e8f0",
    boxShadow: theme.palette.type === "dark" 
      ? "0 2px 8px rgba(0,0,0,0.2)" 
      : "0 2px 8px rgba(0,0,0,0.04)",
  },

  settingsIcon: {
    alignSelf: "center",
    marginLeft: "auto",
    padding: 8,
    borderRadius: 8,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" 
        ? "rgba(255,255,255,0.1)" 
        : "rgba(0,0,0,0.05)",
    },
  },

  tabWithGroups: {
    minWidth: 90,
    width: 90,
    [theme.breakpoints.down("xs")]: {
      minWidth: 64,
      width: 64,
    },
  },

  tab: {
    minWidth: 120,
    width: 120,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 13,
    borderRadius: 8,
    margin: "4px 2px",
    transition: "all 0.2s ease",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.7)" : "#64748b",
    [theme.breakpoints.down("xs")]: {
      minWidth: 72,
      width: 72,
      fontSize: 11,
      padding: "4px 2px",
    },
    "&.Mui-selected": {
      color: theme.palette.type === "dark" ? "#fff" : "#128c7e",
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(37, 211, 102, 0.22)"
        : "rgba(37, 211, 102, 0.12)",
    },
  },

  ticketOptionsBox: {
    display: "flex !important",
    justifyContent: "flex-start",
    alignItems: "center",
    // 2026-06-10 fix: with the new channel filter there are now 5 filter
    // dropdowns (Tags / Users / Connections / Queues / Channel). The old
    // `nowrap` + horizontal-scroll behaviour ran them off the edge. Wrap
    // cleanly to a second row instead — fits one row on wide screens, two
    // tidy rows when narrow.
    flexWrap: "wrap",
    rowGap: theme.spacing(1),
    padding: theme.spacing(1.5),
    gap: theme.spacing(1),
    backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
    borderBottom: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid #e2e8f0",
    marginBottom: theme.spacing(0.5),
    "& > *": { flexShrink: 0 },
    // 2026-06-04: filter dropdown placeholders (Queues / Connections /
    // Users / Tags) are rendered inside <em> tags by their respective
    // child filter components, which inherits the browser default
    // italic font-style and reads as broken UI. Force normal font-style
    // on the Select root + the <em> child so the placeholders render
    // upright regardless of viewport. !important defeats the user-agent
    // stylesheet on <em>.
    "& .MuiSelect-root, & .MuiSelect-root em, & em": {
      fontStyle: "normal !important",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
      gap: theme.spacing(0.5),
      // Compact filter dropdowns on mobile so all four (Connections,
      // Users, Tags, Queues) fit on a single row. !important is
      // needed because FormControl fullWidth prop adds inline-style
      // width:100% which beats normal class rules.
      "& .MuiSelect-outlined": {
        padding: "4px 24px 4px 8px !important",
        fontSize: "11px !important",
        fontStyle: "normal !important",
      },
      "& .MuiFormControl-root": {
        margin: "0 !important",
        minWidth: "0 !important",
        width: "auto !important",
      },
      "& .MuiBox-root": {
        minWidth: "0 !important",
      },
    },
    // Mobile (<600px) the filter chips were still ~56px tall: outlined
    // notch + dense margin + the FormControl's default 8px top inset
    // stacked into a too-bulky row that ate the viewport width with 4
    // pills. Force a 28px minHeight, 0.7rem labels, 2px/8px padding,
    // and drop the outline radius/inset so the chips read as compact
    // toolbar dropdowns rather than full-size form fields.
    [theme.breakpoints.down("xs")]: {
      padding: "4px 6px",
      gap: 4,
      "& .MuiFormControl-root": {
        margin: "0 !important",
        minWidth: "0 !important",
        width: "auto !important",
      },
      "& .MuiInputBase-root": {
        minHeight: 28,
        alignItems: "center",
      },
      "& .MuiSelect-outlined": {
        padding: "4px 22px 4px 10px !important",
        fontSize: "0.7rem !important",
        lineHeight: 1,
        minHeight: 24,
        fontStyle: "normal !important",
        display: "flex",
        alignItems: "center",
        height: "100%",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: 14,
      },
      "& .MuiSelect-icon": {
        right: 2,
      },
    },
  },

  // 2026-06-04: shrink the "Chat Tickets" header bar on xs. Inline-style
  // padding 16px/20px + 20px gradient-title + two 40-ish px IconButtons
  // stacked to ~80-90px above the search/filters on a 360x640 viewport,
  // eating space before the ticket list. xs drops vertical padding to
  // 6px, horizontal to 10px, tightens the row gap, shrinks the title to
  // 15px, and downsizes the action IconButtons to 32x32 with 18px icons.
  headerBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    [theme.breakpoints.down("xs")]: {
      padding: '6px 10px',
    },
  },
  titleText: {
    fontSize: '20px',
    fontWeight: 800,
    letterSpacing: -0.3,
    background: 'linear-gradient(135deg, #075e54 0%, #25d366 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    [theme.breakpoints.down("xs")]: {
      fontSize: '15px',
      letterSpacing: -0.2,
    },
  },
  headerActions: {
    display: 'flex',
    gap: 8,
    [theme.breakpoints.down("xs")]: {
      gap: 4,
    },
  },
  filterIconBtn: {
    borderRadius: 10,
    padding: 8,
    transition: 'all 0.2s ease',
    [theme.breakpoints.down("xs")]: {
      padding: 6,
      borderRadius: 8,
    },
  },
  addIconBtn: {
    background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
    borderRadius: 10,
    padding: 8,
    boxShadow: '0 4px 12px rgba(37, 211, 102, 0.4)',
    transition: 'all 0.2s ease',
    [theme.breakpoints.down("xs")]: {
      padding: 6,
      borderRadius: 8,
      boxShadow: '0 2px 6px rgba(37, 211, 102, 0.35)',
    },
  },
  headerIconSvg: {
    fontSize: 22,
    [theme.breakpoints.down("xs")]: {
      fontSize: 18,
    },
  },
  // The wrapper div around the search row carried inline padding 8px 12px
  // — fine on tablet+ but added another ~16px vertical alongside the
  // serachInputWrapper's 12px auto margin. On xs we drop the outer
  // padding to 4px so the search bar sits flush under the header.
  searchSection: {
    padding: '8px 12px',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    [theme.breakpoints.down("xs")]: {
      padding: '4px 8px',
    },
  },

  serachInputWrapper: {
    flex: 1,
    display: 'flex',
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.06)"
      : "#f8fafc",
    padding: '12px 16px',
    borderRadius: 12,
    maxWidth: '95%',
    margin: '12px auto',
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.1)"
      : "1px solid #e2e8f0",
    transition: "all 0.2s ease",
    "&:focus-within": {
      boxShadow: theme.palette.type === "dark"
        ? "0 0 0 3px rgba(37, 211, 102, 0.32)"
        : "0 0 0 3px rgba(37, 211, 102, 0.18)",
      borderColor: "#25d366",
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(37, 211, 102, 0.10)"
        : "#fff",
    },
    // 2026-06-04: was ~52px tall (12px+16px+12px+input) on xs — shrink
    // to ~32px with 5px/10px padding, 6px radius, 6px auto margin.
    [theme.breakpoints.down("xs")]: {
      padding: '5px 10px',
      borderRadius: 8,
      margin: '6px auto',
      maxWidth: '100%',
    },
  },

  searchIcon: {
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af",
    marginLeft: 4,
    marginRight: 8,
    alignSelf: "center",
    fontSize: 20,
    [theme.breakpoints.down("xs")]: {
      fontSize: 16,
      marginLeft: 2,
      marginRight: 6,
    },
  },

  searchInput: {
    flex: 1,
    border: "none",
    borderRadius: 30,
    fontSize: 14,
    [theme.breakpoints.down("xs")]: {
      fontSize: 16, // >=16px: iOS won't zoom the viewport on focus
    },
    color: theme.palette.type === "dark" ? "#fff" : "#1f2937",
    backgroundColor: "transparent",
    "& input": {
      backgroundColor: "transparent",
    },
    "&::placeholder": {
      color: theme.palette.type === "dark" ? "rgba(255,255,255,0.4)" : "#9ca3af",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: 13,
      "& input": {
        padding: '2px 0',
      },
    },
  },

  badge: {
    // Was absolutely positioned above the tab pill, causing the count
    // chip to "float" over the segmented-control rounded corner and
    // break vertical alignment. Inline next to the label keeps the
    // count anchored to the tab text and prevents the visual jitter
    // when the count changes.
    position: 'relative',
    display: 'inline-flex',
    marginLeft: 6,
    '& .MuiBadge-badge': {
      position: 'static',
      transform: 'none',
      fontSize: '10px',
      fontWeight: 700,
      minWidth: 18,
      height: 18,
      padding: '0 5px',
      borderRadius: 9,
      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      boxShadow: "0 2px 6px rgba(239, 68, 68, 0.4)",
    }
  },
  combinedBadge: {
    // Floats above the pill — original "before" placement that
    // worked fine when the tickets panel had a stable width. Every
    // other variant (inline, top-right tucked, etc.) collided with
    // either the label text or the next pill. The remaining reflow
    // glitch is handled separately by pinning the tickets panel to
    // a fixed width.
    position: 'absolute',
    top: -10,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    borderRadius: 999,
    fontSize: 9,
    fontWeight: 700,
    zIndex: 1,
    overflow: 'hidden',
    boxShadow: '0 2px 6px rgba(0,0,0,0.22)',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  },
  badgeLeft: {
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: '#fff',
    padding: '3px 6px',
    display: 'flex',
    alignItems: 'center',
  },
  badgeRight: {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: '#fff',
    padding: '3px 6px',
    display: 'flex',
    alignItems: 'center',
  },
  show: {
    display: "block",
  },
  hide: {
    display: "none !important",
  },

  icon24: {
    width: 24,
    height: 24,
  },
  radioInputs: {
    position: 'relative',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    borderRadius: 14,
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.06)"
      : "#f1f5f9",
    boxSizing: 'border-box',
    boxShadow: theme.palette.type === "dark"
      ? "inset 0 2px 4px rgba(0,0,0,0.3)"
      : "inset 0 2px 4px rgba(0,0,0,0.06)",
    padding: '6px',
    width: 'auto',
    maxWidth: '100%',
    margin: '16px auto',
    fontSize: '13px',
    gap: '4px',
    rowGap: '4px',
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.1)"
      : "1px solid #e2e8f0",
    [theme.breakpoints.down("sm")]: {
      // Keep horizontal margin auto so the row stays centered on
      // mobile too — fixed '8px' here used to push the whole tab row
      // to one side, making it look offset.
      margin: '12px auto',
      padding: '4px',
      gap: '2px',
    },
  },
  radio: {
    flex: '0 0 auto',
    textAlign: 'center',
  },
  manageBtn: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: 10,
    padding: '6px 8px',
    marginLeft: 4,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "#94a3b8",
    transition: 'all 0.15s ease',
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.04)",
      color: theme.palette.type === "dark" ? "#fff" : "#475569",
    },
  },
  manageMenuRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  manageMenuActions: {
    marginLeft: 'auto',
    display: 'flex',
    gap: 2,
  },
  radioInput: {
    display: 'none',
  },
  radioName: {
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    border: 'none',
    padding: '8px 14px',
    fontWeight: 600,
    fontSize: '12px',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down("sm")]: {
      padding: '6px 10px',
      fontSize: '11px',
    },
  },
  radioNameDefault: {
    backgroundColor: 'transparent',
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.7)" : "#64748b",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" 
        ? "rgba(255,255,255,0.12)" 
        : "rgba(0,0,0,0.04)",
      color: theme.palette.type === "dark" ? "#fff" : "#1f2937",
    },
  },
  radioNameHasActivity: {
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(74, 222, 128, 0.15)"
      : "rgba(34, 197, 94, 0.1)",
    color: theme.palette.type === "dark" ? "#4ade80" : "#16a34a",
    fontWeight: 700,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(74, 222, 128, 0.25)"
        : "rgba(34, 197, 94, 0.18)",
    },
  },
  radioNameActive: {
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #25d366 0%, #128c7e 100%)"
      : "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
    color: '#fff',
    boxShadow: theme.palette.type === "dark"
      ? "0 6px 18px rgba(37, 211, 102, 0.55)"
      : "0 6px 16px rgba(37, 211, 102, 0.45)",
    transform: "scale(1.02)",
    "&:hover": {
      boxShadow: theme.palette.type === "dark"
        ? "0 8px 22px rgba(37, 211, 102, 0.65)"
        : "0 8px 20px rgba(37, 211, 102, 0.55)",
    },
  },
  radioNameChecked: {
    backgroundColor: '#1c1c1c',
    fontWeight: 700,
    color: '#fff',
    padding: '2px'
  },
}));

const TicketsManagerTabs = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [statusParam, setStatusParam] = useState("pending");

  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);

  // Restore channel filter from URL on mount (preserves filter across page reloads).
  const [selectedChannel, setSelectedChannel] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("channel") || null;
  });
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);
  const { profile } = user;
  const [ticketCounters, setTicketCounters] = useState();

  // Sync selectedChannel to URL ?channel= param so the filter survives page reload.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (selectedChannel) {
      params.set("channel", selectedChannel);
    } else {
      params.delete("channel");
    }
    const newSearch = params.toString();
    const current = location.search.replace(/^\?/, "");
    if (newSearch !== current) {
      history.replace({ search: newSearch ? `?${newSearch}` : "" });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannel]);

  // The four built-in tabs (Open / Pending / Closed / All) are ALWAYS
  // rendered regardless of API state — they map 1:1 to the legacy status
  // routing and must never disappear (e.g. before the sections API
  // responds, or if the call fails). The API only contributes:
  //   * an optional renamed display label per system tab (rows with
  //     type='system' and matching systemKey)
  //   * any number of additional custom tag-driven sections that the
  //     user has created on top of the four defaults.
  //
  // Seed from localStorage so custom sections appear on the FIRST render
  // (no flash / "tacked-on" effect on page refresh). The API call still
  // runs immediately; it just hydrates state that's already correct.
  const SECTIONS_CACHE_KEY = "wz_ticket_sections_cache";
  const [sections, setSections] = useState(() => {
    try {
      const cached = localStorage.getItem(SECTIONS_CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  // Per-section ticket counts keyed by:
  //   - 'open' | 'pending' | 'closed' | 'all' for the four built-in tabs
  //   - `custom-${sectionId}` for user-created tag-driven sections
  // Counts already account for the Kanban exclusion applied in
  // ListTicketsService, so the badge always matches the visible list.
  const [sectionCounts, setSectionCounts] = useState({});
  // activeKey is one of: 'open' | 'pending' | 'closed' | 'all' for system
  // tabs, or `custom-${id}` for user-created sections. Default mirrors
  // the previous hard-coded "open" landing.
  const [activeKey, setActiveKey] = useState("open");

  // Tracks which custom section panels have been visited at least once.
  // Custom TicketsList panels are only MOUNTED after their first activation
  // (lazy mount) — this prevents 5+ simultaneous /tickets HTTP requests on
  // page load. System tabs (open/pending/closed/all) are always mounted
  // since they're the most used and need live socket subscriptions from
  // the start. When a custom tab is first clicked it mounts, subscribes
  // to socket events, and fetches its data — subsequent tab switches just
  // toggle CSS visibility (display:contents / none) with no re-fetch.
  const [activatedSections, setActivatedSections] = useState(() => new Set());
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [confirmDeleteSection, setConfirmDeleteSection] = useState(null);
  const [manageMenuAnchor, setManageMenuAnchor] = useState(null);
  const socketManager = useContext(SocketContext);


  const [openCount, setOpenCount] = useState(0);
  const [userOpenCount, setUserOpenCount] = useState(0);

  const [pendingCount, setPendingCount] = useState(0);
  const [openFilter, setOpenFilter] = useState(true);

  const [openUnread, setOpenUnread] = useState(0);
  const [pendingUnread, setPendingUnread] = useState(0);
  const [tabHasActivity, setTabHasActivity] = useState({ open: false, pending: false });


  const userQueueIds = (user?.queues || []).map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);


  const { getSetting } = useSettings();
  const [showTabGroups, setShowTabGroups] = useState(false);

  useEffect(() => {
    Promise.all([
      getSetting("CheckMsgIsGroup"),
      getSetting("groupsTab")
    ]).then(([ignoreGroups, groupsTab]) => {
      setShowTabGroups(ignoreGroups === "disabled" && groupsTab === "enabled");
    });
  }, []);

  // Fetch sections (auto-seeds defaults server-side on first read) and
  // subscribe to the per-company section channel so multiple sessions stay
  // in sync (e.g. an admin renaming "Open" → "Inbox" on one device shows
  // up immediately everywhere).
  const reloadSections = async () => {
    try {
      const { data } = await api.get("/ticket-sections");
      const fresh = Array.isArray(data) ? data : [];
      setSections(fresh);
      // Update cache so next page load shows custom tabs immediately
      try { localStorage.setItem(SECTIONS_CACHE_KEY, JSON.stringify(fresh)); } catch {}
    } catch (err) {
      // Non-blocking: the four hard-coded baseline tabs render either way.
    }
  };

  // Holds the latest filter context in a ref so reloadSectionCounts
  // (which is bound at mount in the socket effect) sees the current
  // showAll / selectedQueueIds without re-binding on every change.
  const countsFiltersRef = useRef({ showAll: false, selectedQueueIds: [] });
  useEffect(() => {
    countsFiltersRef.current = {
      showAll: !!showAllTickets,
      selectedQueueIds,
    };
  }, [showAllTickets, selectedQueueIds]);

  const reloadSectionCounts = async () => {
    try {
      // Pass the same filter context the visible list is using so
      // badge numbers match the rendered tickets 1:1. Without this
      // the endpoint always returned company-wide totals — a
      // non-admin agent with 2 queues saw "50" badges next to a
      // list of 5 tickets (Bug 2 + Bug 3 Raviv flagged).
      const { showAll, selectedQueueIds: qIds } = countsFiltersRef.current;
      const { data } = await api.get("/ticket-sections/counts", {
        params: {
          showAll: showAll ? "true" : undefined,
          queueIds: qIds && qIds.length > 0 ? JSON.stringify(qIds) : undefined,
        },
      });
      setSectionCounts(data && typeof data === "object" ? data : {});
    } catch (err) {
      // Counts are a UX nice-to-have; failure must not break the tabs.
    }
  };

  useEffect(() => {
    reloadSections();
    reloadSectionCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch counts whenever the user changes the queue filter or
  // flips the "Show all" toggle — otherwise the badges stayed at
  // their previous scope until the next socket event.
  useEffect(() => {
    reloadSectionCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllTickets, selectedQueueIds]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId || !socketManager?.GetSocket) return;
    const socket = socketManager.GetSocket(companyId);

    const sectionEvent = `company-${companyId}-ticket-section`;
    const onSectionEvent = () => {
      reloadSections();
      reloadSectionCounts();
    };
    socket.on(sectionEvent, onSectionEvent);

    // Any ticket mutation (create / status change / tag change / delete)
    // can shift a ticket between tabs AND change the global open /
    // pending / unread totals on the top cards. Refresh BOTH endpoints
    // on every ticket event:
    //   - reloadSectionCounts → /ticket-sections/counts (per-tab badges)
    //   - updateCounters       → /tickets/counters       (global dashboard cards)
    // updateCounters has its own leading-edge throttle (max 1 per 2s)
    // so a flood of socket events still collapses into one HTTP call.
    // The 400ms trailing debounce protects /ticket-sections/counts from
    // a burst (multi-tag rename, bulk close).
    let debounce;
    const scheduleCountRefresh = () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        reloadSectionCounts();
        // Refresh global counters too — previously only sectionCounts
        // updated, leaving liveOpenTickets / livePendingTickets /
        // openUnreadMessages / pendingUnreadMessages stale until the
        // next list-length change in TicketsListCustom (which never
        // fires for in-place ticket updates inside the same tab).
        updateCounters();
      }, 400);
    };
    const ticketEvent = `company-${companyId}-ticket`;
    socket.on(ticketEvent, scheduleCountRefresh);

    return () => {
      socket.off(sectionEvent, onSectionEvent);
      socket.off(ticketEvent, scheduleCountRefresh);
      clearTimeout(debounce);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketManager]);

  // Build the rendered tab list:
  //   - 4 hard-coded system rows (Open / Pending / Closed / All) always
  //     present, in fixed order, with the legacy default labels
  //   - if the API has a row with the same systemKey, override its `name`
  //     and `color` (rename) and attach the real DB id so editing works
  //   - then append all user-created tag-filter sections
  const displaySections = useMemo(() => {
    const baseDefaults = [
      { systemKey: "open", name: t("tickets.tabs.open.title") },
      { systemKey: "pending", name: t("ticketsList.pendingHeader") },
      { systemKey: "closed", name: t("tickets.tabs.closed.title") },
      { systemKey: "all", name: t("tickets.buttons.showAll") },
    ];

    const apiBySysKey = new Map(
      sections
        .filter((s) => s.type === "system" && s.systemKey)
        .map((s) => [s.systemKey, s])
    );

    const systemRows = baseDefaults.map((d) => {
      const fromApi = apiBySysKey.get(d.systemKey);
      return {
        key: d.systemKey,
        id: fromApi?.id || null,
        type: "system",
        systemKey: d.systemKey,
        // System tabs (Open/Pending/Closed/All) use i18n unconditionally.
        // We ignore whatever name the DB has for them so translations
        // can be edited in code (translate/languages/*.js) instead of
        // requiring an UPDATE statement on every locale change.
        // Custom user-created sections still use their DB name (handled
        // in the customRows branch below).
        name: d.name,
        color: fromApi?.color || null,
        tagIds: [],
      };
    });

    const customRows = sections
      .filter((s) => s.type !== "system")
      .map((s) => ({
        key: `custom-${s.id}`,
        id: s.id,
        type: s.type,
        systemKey: null,
        name: s.name,
        color: s.color || null,
        tagIds: Array.isArray(s.tagIds) ? s.tagIds : [],
      }));

    // Order: Open → Pending → custom user tabs → Closed → All.
    // Custom sections sit next to the live action tabs (where the
    // agent does most work), with the archive-style Closed/All
    // tabs pushed to the end so they don't break the active flow.
    const openPending = systemRows.filter(
      r => r.systemKey === "open" || r.systemKey === "pending"
    );
    const closedAll = systemRows.filter(
      r => r.systemKey === "closed" || r.systemKey === "all"
    );
    return [...openPending, ...customRows, ...closedAll];
  }, [sections, t]);

  const activeSection =
    displaySections.find((s) => s.key === activeKey) || displaySections[0];

  const canManageSections =
    profile === "admin" || profile === "supervisor";

  const handleSelectSection = (section) => {
    setActiveKey(section.key);
    // Reset channel filter when switching tabs — each tab starts unfiltered.
    setSelectedChannel(null);
    if (section.type === "system" && section.systemKey) {
      setStatusParam(section.systemKey);
    } else {
      setStatusParam("section");
      // Mark this custom section as activated — triggers lazy mount of its
      // TicketsList panel on the first visit.
      setActivatedSections(prev => new Set([...prev, section.key]));
    }
  };

  const handleEditSection = (section) => {
    // System rows without a backing DB row yet (API hasn't responded or
    // the table is empty) cannot be edited until the lazy-seed completes.
    if (!section.id) {
      reloadSections();
      return;
    }
    setEditingSection({
      id: section.id,
      type: section.type,
      systemKey: section.systemKey,
      name: section.name,
      color: section.color,
      tagIds: section.tagIds,
    });
    setSectionModalOpen(true);
  };

  const handleAddSection = () => {
    setEditingSection(null);
    setSectionModalOpen(true);
  };

  const handleSectionSaved = async () => {
    await reloadSections();
  };

  const handleDeleteSection = async () => {
    if (!confirmDeleteSection || !confirmDeleteSection.id) return;
    try {
      await api.delete(`/ticket-sections/${confirmDeleteSection.id}`);
      toast.success("Section deleted");
      // If the deleted section was active, fall back to the Open tab.
      if (activeKey === confirmDeleteSection.key) {
        setActiveKey("open");
        setStatusParam("open");
      }
      await reloadSections();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    } finally {
      setConfirmDeleteSection(null);
    }
  };

  useEffect(() => {
    // Guard: on an auth/network blip `user` (or user.profile) can be undefined.
    // Crashing here logged the whole app out — degrade gracefully instead.
    const p = (user && user.profile ? user.profile : "").toUpperCase();
    if (p === "ADMIN" || p === "SUPERVISOR") {
      setShowAllTickets(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
    }
  }, [tab]);

  // searchTimeout must be a ref — a plain `let` variable is re-initialised
  // to undefined on every render, so clearTimeout() always received undefined
  // and the debounce never cancelled previous timers. Every keystroke created
  // its own 500 ms timer, causing multiple simultaneous /tickets fetches.
  const searchTimeoutRef = useRef(null);

  // Saves { activeKey, statusParam } before we switch to the "all" view so we
  // can restore the user's previous tab when they clear the search box.
  const prevSearchStateRef = useRef(null);

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    clearTimeout(searchTimeoutRef.current);

    if (searchedTerm) {
      // Entering search mode — snap "all" tab active so results are visible.
      if (statusParam !== 'all') {
        prevSearchStateRef.current = { activeKey, statusParam };
        setStatusParam('all');
        setActiveKey('all');
      }
    } else {
      // Search cleared — restore the tab the user was on before searching.
      if (prevSearchStateRef.current) {
        setStatusParam(prevSearchStateRef.current.statusParam);
        setActiveKey(prevSearchStateRef.current.activeKey);
        prevSearchStateRef.current = null;
      }
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };
  // Leading-edge throttle: socket events + reducer churn used to call
  // this dozens of times per second per tab, hitting the browser's
  // per-host concurrency cap (ERR_INSUFFICIENT_RESOURCES). 2 sec window
  // is fine — counter freshness <2 sec is not user-perceivable.
  const updateCountersLastCallRef = useRef(0);
  const updateCounters = async () => {
    const now = Date.now();
    if (now - updateCountersLastCallRef.current < 2000) return;
    updateCountersLastCallRef.current = now;
    try {
      const { data } = await api.get('/tickets/counters')

      if (data) {
        setTicketCounters(data[0].counters)
        setPendingCount(data[0].counters.livePendingTickets)

        // Filter the attendants array to find the one with the matching user ID
        const matchingAttendant = data[0].attendants.find(attendant => attendant.id === user.id);

        if (matchingAttendant) {
          // Call setUserPending() to set the pendingCount property for the matching attendant
          setUserOpenCount(matchingAttendant.openCount);
        } else {
          console.log(`No attendant found with user ID: ${user.id}`);
        }

      }
    } catch (err) {
      toast.error(err.message);
    }
  }
  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  const handleChangeTabOpen = (e, newValue) => {
    setTabOpen(newValue);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    setSelectedTags(selecteds);
  };
  
  const handleSelectedWhatsapp = (selecteds) => {
    setSelectedWhatsapp(selecteds);
  };

  const handleSelectedUsers = (selecteds) => {
    setSelectedUsers(selecteds);
  };

  return (
    <Paper elevation={0} className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
       
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <Paper elevation={0} className={classes.tabsHeader}>
        <div className={classes.headerBar}>
          <Typography className={classes.titleText}>
            {t("tickets.title")}
          </Typography>
          <div className={classes.headerActions}>
            <IconButton
              size="small"
              onClick={() => setOpenFilter(!openFilter)}
              className={classes.filterIconBtn}
              style={{
                backgroundColor: openFilter ? 'rgba(37, 211, 102, 0.15)' : 'transparent',
              }}
            >
              <FilterListIcon
                className={classes.headerIconSvg}
                style={{ color: openFilter ? '#128c7e' : 'inherit' }}
              />
            </IconButton>

            {/* Phase 3.10 — Omnichannel Dashboard entry */}
            <IconButton
              size="small"
              onClick={() => history.push("/")}
              className={classes.filterIconBtn}
              title="לוח בקרה"
            >
              <BarChartIcon className={classes.headerIconSvg} />
            </IconButton>

            <IconButton
              size="small"
              onClick={() => setNewTicketModalOpen(true)}
              className={classes.addIconBtn}
            >
              <AddCommentIcon
                className={classes.headerIconSvg}
                style={{ color: '#fff' }}
              />
            </IconButton>
          </div>
        </div>
      </Paper>
      {/* Phase 3.9 — UX Refinement: Search (primary) → Filters → Channels → Status
          Search is always visible — agents use it on every session.
          Filters are secondary: gated behind the filter icon toggle.
          Channel chips are segmentation: always visible, below filters.
          Status tabs are workflow: always at bottom. */}

      {/* 1. Search — always visible, primary action */}
      <div className={classes.searchSection}>
        <div className={classes.serachInputWrapper}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            inputRef={searchInputRef}
            placeholder={t("tickets.search.placeholder")}
            type="search"
            onChange={handleSearch}
          />
        </div>
      </div>

      {/* 2. Filters — secondary, collapsible via the filter icon button.
              Includes: תגיות | משתמשים | חיבורים | תורים | ערוצים */}
      <div style={{ backgroundColor: 'transparent' }}>
        <div style={{ display: openFilter ? 'block' : 'none' }}>
          <div
            className={classes.ticketOptionsBox}
            style={{ display: 'flex', flexWrap: 'wrap' }}
          >
            <TagsFilter onFiltered={handleSelectedTags} />
            {(profile === "admin" || profile === "supervisor") && (
              <UsersFilter onFiltered={handleSelectedUsers} />
            )}
            {(profile === "admin" || profile === "supervisor") && (
              <WhatsAppFilter onFiltered={handleSelectedWhatsapp} />
            )}
            <TicketsQueueSelect
              selectedQueueIds={selectedQueueIds}
              userQueues={user?.queues}
              onChange={(values) => setSelectedQueueIds(values)}
            />
            {/* Channel filter — controlled: parent owns state + URL sync */}
            <ChannelsFilter
              value={selectedChannel}
              onFiltered={setSelectedChannel}
            />
          </div>
        </div>
      </div>

      {openFilter && (
        <div className={classes.radioInputs}>
          {displaySections.map((section) => {
            const isActive = activeSection?.key === section.key;
            const sysKey = section.systemKey;
            const isOpenTab = sysKey === "open";
            const isPendingTab = sysKey === "pending";
            const isCustomTab = section.type !== "system";

            // Counter rules (per product spec):
            //   Open    → numeric count of open tickets
            //   Pending → numeric count of pending tickets
            //   Closed  → no badge (volume isn't actionable)
            //   All     → no badge (same)
            //   Custom  → no numeric badge; only an orange unread
            //             indicator when this section has unread
            //             customer activity
            // Raviv 2026-05-30: the previous fallback used ticketCounters.liveOpenTickets
            // (from /tickets/counters — UNFILTERED, company-wide) and pendingCount
            // (same source) when sectionCounts hadn't loaded yet. This caused a
            // visible flash: updateCounters() resolved first (triggered by
            // ticketsList.length change in TicketsListCustom) and set unfiltered
            // values (e.g. 22, 32) while reloadSectionCounts() was still in-flight.
            // The badge briefly showed 22 → then corrected to the filtered 11.
            //
            // Fix: use 0 as fallback. showCountBadge = tabCount > 0, so the badge
            // simply disappears for the ~200ms load window instead of showing a
            // misleading inflated number. sectionCounts IS the authoritative source;
            // no fallback to unfiltered counters is needed or correct.
            let tabCount = 0;
            if (isOpenTab || isPendingTab) {
              tabCount = sectionCounts[section.key] ?? 0;
            }
            const showCountBadge = (isOpenTab || isPendingTab) && tabCount > 0;

            // Unread indicator — all read from `/ticket-sections/counts`
            // so the same Kanban "section claim" rule applies: a
            // ticket assigned to a custom section never inflates the
            // Open/Pending unread number. Falls back to the legacy
            // counters endpoint only if the section endpoint hasn't
            // populated yet (first paint before /counts resolves).
            let unreadCount = 0;
            if (isOpenTab) {
              unreadCount = sectionCounts.open_unread !== undefined
                ? sectionCounts.open_unread
                : (ticketCounters?.openUnreadMessages || 0);
            } else if (isPendingTab) {
              unreadCount = sectionCounts.pending_unread !== undefined
                ? sectionCounts.pending_unread
                : (ticketCounters?.pendingUnreadMessages || 0);
            } else if (isCustomTab) {
              unreadCount = sectionCounts[`${section.key}_unread`] || 0;
            }
            const showUnread = !isActive && unreadCount > 0;

            return (
              <label
                key={section.key}
                className={classes.radio}
                style={{ position: "relative" }}
              >
                {/* Badge has to live in the <label>, NOT inside the
                    radioName <span>, because radioName carries an
                    overflow:hidden that clipped the floating chip
                    (only the bottom red sliver showed). The label
                    itself has no clipping, so a chip absolutely
                    positioned at top:-10 stays fully visible above
                    the pill. */}
                {showCountBadge && (
                  <span className={classes.combinedBadge}>
                    <span className={classes.badgeLeft}>{tabCount}</span>
                    {showUnread && (
                      <span className={classes.badgeRight}>{unreadCount}</span>
                    )}
                  </span>
                )}
                {!showCountBadge && showUnread && isCustomTab && (
                  <span className={classes.combinedBadge}>
                    <span className={classes.badgeRight}>{unreadCount}</span>
                  </span>
                )}
                <input
                  type="radio"
                  name="status"
                  value={section.key}
                  checked={isActive}
                  onChange={() => handleSelectSection(section)}
                  className={classes.radioInput}
                />
                <span
                  className={`${classes.radioName} ${
                    isActive ? classes.radioNameActive : classes.radioNameDefault
                  }`}
                  style={
                    section.color && isActive
                      ? { background: section.color, color: "#fff" }
                      : undefined
                  }
                  title={section.name}
                >
                  {section.name}
                </span>
              </label>
            );
          })}

          {canManageSections && (
            <span
              className={classes.manageBtn}
              onClick={(e) => setManageMenuAnchor(e.currentTarget)}
              title="Manage sections"
            >
              <MoreHorizIcon style={{ fontSize: 18 }} />
            </span>
          )}
        </div>
      )}

      <Menu
        anchorEl={manageMenuAnchor}
        open={Boolean(manageMenuAnchor)}
        onClose={() => setManageMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        getContentAnchorEl={null}
        PaperProps={{ style: { minWidth: 260, maxHeight: 360 } }}
      >
        <MenuItem
          onClick={() => {
            setManageMenuAnchor(null);
            handleAddSection();
          }}
        >
          <ListItemIcon style={{ minWidth: 32 }}>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Add new section" />
        </MenuItem>
        <Divider />
        {displaySections.map((section) => {
          const isCustom = section.type !== "system";
          return (
            <MenuItem
              key={`menu-${section.key}`}
              dense
              disableRipple
              style={{ cursor: "default" }}
            >
              <div className={classes.manageMenuRow} style={{ width: "100%" }}>
                <span style={{ flex: 1, fontSize: 13 }}>
                  {section.name}
                  {!isCustom && (
                    <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.55 }}>
                      (built-in)
                    </span>
                  )}
                </span>
                <div className={classes.manageMenuActions}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setManageMenuAnchor(null);
                      handleEditSection(section);
                    }}
                    title="Rename"
                  >
                    <EditIcon style={{ fontSize: 16 }} />
                  </IconButton>
                  {isCustom && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setManageMenuAnchor(null);
                        setConfirmDeleteSection(section);
                      }}
                      title="Delete section"
                    >
                      <DeleteIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </div>
              </div>
            </MenuItem>
          );
        })}
      </Menu>

      {/* All panels stay mounted; CSS toggles which is visible so a
          ticket moved to a different section appears in the target
          tab live, without a tab-switch refresh round-trip.

          Visible / hidden styles per Raviv's note (2026-05-13): the
          previous display:contents / display:none toggle removed the
          hidden panel from the layout entirely, which left the
          browser with no scroll-container height to remeasure when
          the panel later became visible — Closed pagination (and any
          other tab-switched-to-after-mount) loaded ~5 tickets and
          never fetched page 2 on scroll because the scroll listener
          couldn't compute scrollHeight − scrollTop − clientHeight.
          Switching to height-based toggle keeps both panels in the
          layout (active = height:100%, inactive = height:0 +
          visibility:hidden) so the scroll container always has a
          measurable height the moment it becomes visible. */}
      {(() => {
        const isActiveSystem = (key) =>
          activeSection && activeSection.type === "system" && activeSection.systemKey === key;
        // Stable object references (defined once per render cycle, not per
        // panel) so the div's style prop doesn't flip to a new object on
        // every parent re-render — which was causing TicketsList to receive
        // a "changed" style prop and triggering an unnecessary re-render on
        // every socket event.  The visibility:hidden keeps the panel in the
        // layout flow (height:0 alone collapses scroll container, height-
        // based toggle comment at line ~1270 explains why display:none was
        // ruled out).
        const visible = { height: "100%", overflow: "hidden" };
        const hidden = { height: 0, overflow: "hidden", visibility: "hidden" };
        return (
          <>
            <div style={isActiveSystem("all") ? visible : hidden}>
              <TicketsList
                showAll={true}
                searchParam={searchParam}
                tags={selectedTags}
                whatsapps={selectedWhatsapp}
                users={selectedUsers}
                selectedQueueIds={selectedQueueIds}
                updateCount={updateCounters}
                sections={sections}
                channel={selectedChannel}

              />
            </div>
            <div style={isActiveSystem("open") ? visible : hidden}>
              <TicketsList
                status="open"
                showAll={showAllTickets}
                selectedQueueIds={selectedQueueIds}
                updateCount={updateCounters}
                tags={selectedTags}
                setTabOpen={setTabOpen}
                whatsapps={selectedWhatsapp}
                users={selectedUsers}
                setStatusParam={setStatusParam}
                sections={sections}
                channel={selectedChannel}

              />
            </div>
            <div style={isActiveSystem("pending") ? visible : hidden}>
              <TicketsList
                status="pending"
                showAll={showAllTickets}
                setTabOpen={setTabOpen}
                tags={selectedTags}
                users={selectedUsers}
                whatsapps={selectedWhatsapp}
                selectedQueueIds={selectedQueueIds}
                setStatusParam={setStatusParam}
                updateCount={updateCounters}
                sections={sections}
                channel={selectedChannel}

              />
            </div>
            <div style={isActiveSystem("closed") ? visible : hidden}>
              <TicketsList
                status="closed"
                showAll={true}
                tags={selectedTags}
                whatsapps={selectedWhatsapp}
                users={selectedUsers}
                selectedQueueIds={selectedQueueIds}
                updateCount={updateCounters}
                sections={sections}
                channel={selectedChannel}

              />
            </div>
            {displaySections
              .filter((s) => s.type === "tag-filter" && s.id)
              .map((s) => {
                // Lazy mount: don't create the TicketsList DOM + fetch until
                // the user has clicked this section at least once. Prevents
                // N simultaneous /tickets requests on page load (one per
                // custom section). After the first visit the panel stays
                // mounted — only CSS visibility toggles on subsequent switches.
                if (!activatedSections.has(s.key)) return null;
                return (
                  <div
                    key={`section-panel-${s.id}`}
                    style={activeSection && activeSection.key === s.key ? visible : hidden}
                  >
                    <TicketsList
                      showAll={true}
                      searchParam={searchParam}
                      tags={selectedTags}
                      whatsapps={selectedWhatsapp}
                      users={selectedUsers}
                      selectedQueueIds={selectedQueueIds}
                      updateCount={updateCounters}
                      sectionId={s.id}
                      sections={sections}
                      channel={selectedChannel}
      
                    />
                  </div>
                );
              })}
          </>
        );
      })()}

      <TicketSectionModal
        open={sectionModalOpen}
        onClose={() => setSectionModalOpen(false)}
        section={editingSection}
        onSaved={handleSectionSaved}
      />

      <ConfirmationModal
        title={
          confirmDeleteSection
            ? `Delete section "${confirmDeleteSection.name}"?`
            : ""
        }
        open={Boolean(confirmDeleteSection)}
        onClose={() => setConfirmDeleteSection(null)}
        onConfirm={handleDeleteSection}
      >
        This will remove the section. Tickets and tags are not affected.
      </ConfirmationModal>
    </Paper>
  );
};

export default TicketsManagerTabs;
